import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { UserRole } from "@repo/database";
import { createFileRoute } from "@tanstack/react-router";
import { VisuallyHidden } from "react-aria";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import MerchantForm from "~/components/admin/merchant-form";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { api } from "~/lib/api";
import { useAuthStore } from "~/stores/use-auth-store";

export const Route = createFileRoute("/_main/admin/merchants/")({
  component: MerchantsPage,
});

function MerchantsPage() {
  const { t } = useTranslation("common");

  const [newMerchantOpen, setNewMerchantOpen] = useState(false);

  const { data: user } = api.user.getUser.useQuery();

  const { data: users } = api.user.listUsers.useQuery(undefined, {
    enabled: user?.role === UserRole.admin,
  });

  const columns: ColumnDef<NonNullable<typeof users>[number]>[] = [
    {
      id: t("User.company_name"),
      accessorKey: "companyName",
      filterFn: "includesString",
      size: 10,

      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("User.company_name")} />
      ),
      cell: ({ row: { original: user } }) => {
        return <p>{user.companyName}</p>;
      },
    },
  ];

  const { setImpersonateId } = useAuthStore(
    useShallow((state) => ({
      setImpersonateId: state.setImpersonateId,
    })),
  );

  if (user?.role !== UserRole.admin) {
    return <span className="text-4xl font-bold">NO ACCESS</span>;
  }

  return (
    <div>
      <Dialog open={newMerchantOpen} onOpenChange={setNewMerchantOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Merchant</DialogTitle>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          <div className="overflow-auto max-h-[80vh] small-scrollbar p-4">
            <MerchantForm />
          </div>
          <DialogFooter>
            <Button
              form="merchant-form"
              type="submit"
              variant="success"
              className="w-full"
            >
              Create merchant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SettingsHeader
        buttonLabel="New merchant"
        buttonAction={() => {
          setNewMerchantOpen(true);
        }}
        title={t("Admin.Merchants.page_title")}
      />
      <DataTable
        onSelect={(row) => {
          setImpersonateId(row.id);
        }}
        columns={columns}
        data={users ?? []}
      />
    </div>
  );
}
